import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { navLinks } from "@/data/mock";

const MEMBERS_AREA_URL = "https://app.lifely.guide";
const JOIN_URL = "https://app.lifely.guide/join/pricing";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isTransparent = isHome && !scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-background/80 backdrop-blur-2xl border-b border-border/40 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-6xl mx-auto px-6 h-18 flex items-center justify-between py-4">
        <Link
          to="/"
          className={`font-serif text-2xl tracking-tight hover:opacity-80 transition-all ${
            isTransparent ? "text-white" : "text-foreground"
          }`}
        >
          Lifely
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-[0.9rem] transition-colors duration-200 relative ${
                isTransparent
                  ? location.pathname === link.path
                    ? "text-white"
                    : "text-white/70 hover:text-white"
                  : location.pathname === link.path
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {link.label}
              {location.pathname === link.path && (
                <motion.div
                  layoutId="nav-underline"
                  className={`absolute -bottom-1 left-0 right-0 h-px ${isTransparent ? "bg-white/60" : "bg-primary/60"}`}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
            </Link>
          ))}
          <a
            href={JOIN_URL}
            className={`text-[0.9rem] flex items-center gap-1.5 transition-opacity font-medium px-4 py-2 rounded-full ${
              isTransparent 
                ? "bg-white text-[#2D2A26] hover:bg-white/90" 
                : "bg-foreground text-background hover:bg-foreground/90"
            }`}
          >
            Join
          </a>
          <a
            href={MEMBERS_AREA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-[0.9rem] flex items-center gap-1.5 hover:opacity-70 transition-opacity font-medium ${
              isTransparent ? "text-white" : "text-primary"
            }`}
          >
            Members area <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>

        <button
          className={`md:hidden p-2 -mr-2 ${isTransparent ? "text-white" : "text-foreground"}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <X className="w-5 h-5" />
          ) : (
            <Menu className="w-5 h-5" />
          )}
        </button>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="md:hidden bg-background/95 backdrop-blur-2xl border-b border-border/40 overflow-hidden"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-[0.95rem] py-3 transition-colors border-b border-border/30 last:border-0 ${
                    location.pathname === link.path
                      ? "text-foreground font-medium"
                      : "text-muted-foreground"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href={JOIN_URL}
                className="text-[0.95rem] py-3 text-background bg-foreground px-4 rounded-full flex items-center justify-center font-medium"
              >
                Join
              </a>
              <a
                href={MEMBERS_AREA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[0.95rem] py-3 text-primary flex items-center gap-1.5 font-medium text-left"
              >
                Members area <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
